import React, { useState, useEffect, useRef } from 'react'
import { css } from '@linaria/core'
import { colors } from '@fs/zion-ui'
import { sanitizeProps, boolAttr } from '@fs/zion-frontend-friends'
import { Link } from '@fs/zion-router'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import TileScrim from './components/TileScrim'
import TileInfo from './components/TileInfo'

const tileCss = css`
  position: relative;
  background-color: ${colors.gray70};
  background-size: cover;
  background-position: center 10%;
  background-repeat: no-repeat;
  min-width: 68px;
  max-width: 320px;
  padding-bottom: min(100%, 320px);

  &[interactable] {
    cursor: pointer;
  }
`

const removedButtonCss = css`
  all: unset;
  &:focus {
    outline: 2px solid ${colors.blue30};
  }
`

const memoryKeyMap = {
  photo: 'memory-tile.aria-label.type-photo',
  document: 'memory-tile.aria-label.type-document',
  story: 'memory-tile.aria-label.type-story',
  audio: 'memory-tile.aria-label.type-audio',
}

export function MemoryTileBase({ image, type, duration, count, viewAllCount, interactable, ...props }) {
  const [size, setSize] = useState('lg')
  const tileRef = useRef()

  useEffect(() => {
    // Tests do not have ResizeObserver defined
    if (typeof window === 'undefined' || !window?.ResizeObserver) return undefined
    const observer = new window.ResizeObserver(() => {
      setSize(tileRef.current?.clientWidth > 133 ? 'lg' : 'sm')
    })
    observer.observe(tileRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      className={tileCss}
      tile-size={size}
      interactable={boolAttr(interactable)}
      data-testid="memory-tile"
      style={{
        backgroundImage: image ? `url(${image})` : undefined,
      }}
      ref={tileRef}
      {...sanitizeProps(props)}
    >
      {type !== 'photo' && image && <TileScrim fade={size === 'lg'} />}

      <TileInfo
        type={type}
        size={size}
        withImage={!!image}
        duration={duration}
        count={count}
        viewAllCount={viewAllCount}
      />
    </div>
  )
}

// This component just determines if it is wrapped in a Link or button - the MemoryTileBase does all the heavy lifting.
export default function MemoryTile({ to, external, action, linkName, target, onClick, ...props }) {
  const { viewAllCount, type } = props
  const [t] = useTranslation()
  // Since we are creating an empty title we need to put an aria-label on the action
  const ariaLabel = viewAllCount ? t('memory-tile.aria-label.view-all') : t(memoryKeyMap[type])

  if (to) {
    return (
      <Link
        aria-label={ariaLabel}
        to={to}
        external={external}
        action={action}
        linkName={linkName}
        onClick={onClick}
        noLinkStyles
        target={target}
        data-testid="memory-tile-link"
      >
        <MemoryTileBase {...props} interactable />
      </Link>
    )
  }

  if (onClick) {
    return (
      <button className={removedButtonCss} type="button" onClick={onClick} aria-label={ariaLabel}>
        <MemoryTileBase {...props} interactable />
      </button>
    )
  }

  return <MemoryTileBase {...props} />
}

MemoryTileBase.propTypes = {
  /** URL to the image for the tile. */
  image: PropTypes.string,

  /** The type of memory being displayed. */
  type: PropTypes.oneOf(['photo', 'document', 'story', 'audio']),

  /** Should be formatted as `mm:ss` (mins and seconds). It is not formatted for you. */
  duration: PropTypes.string,

  /** Applies to type "story" only. Indicates the number of photos attached to the story. */
  count: PropTypes.number,

  /** If provided, adds the "View All" scrim, count, and text on the tile. */
  viewAllCount: PropTypes.number,
}
